import type { FunctionComponent } from 'react';

import { NumberInput } from '@mantine/core';
import type { UseFormReturnType } from '@mantine/form';
import { MODIFIER_MINIMUM, MODIFIER_MAXIMUM, NON_REQUIRED_FIELDS } from 'constants/menuManagement';

import { ClearInputButton } from 'components/MenuManagement/ClearInputButton';
import { RIGHT_SECTION_WIDTH } from 'components/MenuManagement/inputConstants';
import { useTranslation } from 'i18n';

const NS = 'menuManagementFields';

export type NumberInputFormValues = {
  [key: string]: string | number | null | undefined;
};

type NumberInputFieldProps = {
  title?: string;
  dataIndex: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  record?: Record<string, any>;
  disabledDueToLinkedMerchants?: boolean;
  menuInfo?: boolean;
  required?: boolean;
  form: UseFormReturnType<NumberInputFormValues>;
};

export const NumberInputField: FunctionComponent<NumberInputFieldProps> = ({
  title,
  dataIndex,
  record,
  disabledDueToLinkedMerchants = false,
  menuInfo = true,
  required = false,
  form,
}) => {
  const { t } = useTranslation(NS);

  const minNonDrawer =
    dataIndex === 'maximum' ? ((record?.minimum as number) ?? MODIFIER_MINIMUM) : MODIFIER_MINIMUM;

  const getTitleToUse = () => {
    if (dataIndex === 'maximum') return t('NumberInputField.maximumLabel');
    if (dataIndex === 'minimum') return t('NumberInputField.minimumLabel');
    return title;
  };

  const titleToUse = getTitleToUse();

  const maxNonDrawer =
    dataIndex === 'minimum' ? ((record?.maximum as number) ?? MODIFIER_MAXIMUM) : MODIFIER_MAXIMUM;

  const min = menuInfo ? MODIFIER_MINIMUM : minNonDrawer;
  const max = menuInfo ? MODIFIER_MAXIMUM : maxNonDrawer;

  const showClear = Boolean(form.values[dataIndex]) && !disabledDueToLinkedMerchants;

  return (
    <NumberInput
      /* eslint-disable-next-line react/jsx-props-no-spreading */
      {...form.getInputProps(dataIndex)}
      // Coalesce null/undefined to '' so Mantine's NumberInput visually clears
      // (it ignores a null value and would otherwise keep showing the old number).
      value={form.values[dataIndex] ?? ''}
      label={menuInfo ? titleToUse : null}
      min={min}
      max={max}
      disabled={disabledDueToLinkedMerchants}
      placeholder={
        disabledDueToLinkedMerchants
          ? (record?.[dataIndex] as string | number | undefined)?.toString()
          : undefined
      }
      required={!NON_REQUIRED_FIELDS.includes(dataIndex) || required}
      rightSectionWidth={RIGHT_SECTION_WIDTH}
      rightSection={
        <ClearInputButton onClick={() => form.setFieldValue(dataIndex, null)} show={showClear} />
      }
      size="sm"
      mb="xs2"
    />
  );
};
